import {AfterViewInit, Component, ElementRef, HostListener, Input, OnInit, ViewChild} from "@angular/core";
import {PhotovoltaikcheckAnfrage} from "../anfrage/photovoltaikcheck.anfrage";
import {Result} from "../interfaces/result";
import {PortalService} from "../../services/portal.service";
import {Configuration, Optimierung} from "../interfaces/configuration";
import {PhotovoltaikcheckRequestService} from "../services/request.service";
import {FormControl, FormGroup} from "@angular/forms";
import {PhotovoltaikcheckBackendService} from "../services/backend.service";
import {ElementService} from "../../services/element.service";

/**
 * Result coponents of the balcony path of the pvc
 */
@Component({
  selector: 'app-photovoltaikcheck-ergebnis-balcony',
  templateUrl: './ergebnis.balcony.component.html',
  providers: []
})
export class ErgebnisBalconyComponent implements OnInit, AfterViewInit {
  /**
   * prefix for all texts
   */
  @Input() textkeyPrefix: string;

  /**
   * the request object
   */
  @Input() anfrage: PhotovoltaikcheckAnfrage;

  /**
   * Backcard of the price flipper
   */
  @ViewChild('backCardPrice') backCardPrice: ElementRef;

  /**
   * Frontcard of the price flipper
   */
  @ViewChild('frontCardPrice') frontCardPrice: ElementRef;

  /**
   * Backcard of the compare flipper
   */
  @ViewChild('backCardCompare') backCardCompare: ElementRef;

  /**
   * Frontcard of the compare flipper
   */
  @ViewChild('frontCardCompare') frontCardCompare: ElementRef;

  /**
   * Wrapper for the systems we show
   */
  @ViewChild('devices') devices: ElementRef;

  /**
   * min height of the price element to avoid overlay after flipping
   */
  minHeightPrice: number = 0;

  /**
   * min height of the compare element to avoid overlay after flipping
   */
  minHeightCompare: number = null;

  /**
   * form for toggles
   */
  form: FormGroup = new FormGroup({
    verschattung: new FormControl(null, []),
    batteriespeicher: new FormControl(null, [])
  });

  /**
   * available configurations. They are fix.
   */
  configurations: {
    klein: {type: 'klein', config: Configuration}
    gross: {type: 'gross', config: Configuration}
    gross_batterie: {type: 'gross_batterie', config: Configuration}
  };

  /**
   * available types of configurations
   */
  types = ['klein', 'gross', 'gross_batterie'];

  /**
   * Types we want to show in the buttons
   */
  buttonsTypes = ['klein', 'gross_batterie'];

  /**
   * currently selected configurations
   */
  currentConfiguration: Optimierung;

  /**
   * result object from the backend.
   */
  result: Result;

  /**
   * toggle if we have an internal portal or not.
   */
  internalPortal: boolean = false;

  /**
   * toggle for the loading animations
   */
  loading: boolean = false;

  /**
   * some toggles to show/hide elements
   */
  toggle = {
    compare: false,
    shadedHelp: false,
    investHelp: false,
    price: false,
    loading: false
  };

  /**
   * plaeholder for the curren selected configuration.
   */
  placeholder?: {[key: string]: string | number};

  /**
   * constructor
   * @param portalService
   * @param requestService
   * @param backendService
   * @param elementService
   */
  constructor(private portalService: PortalService, private requestService: PhotovoltaikcheckRequestService,
              private backendService: PhotovoltaikcheckBackendService, private elementService: ElementService) {
  }

  /**
   * @inheritdoc
   */
  ngOnInit() {
    this.setSelection();

    this.internalPortal = this.portalService.isInternalPortal();

    // listen for changes in the form
    this.form.get('verschattung').valueChanges.subscribe(() => {
      this.reCalculate();
    });

    this.form.get('batteriespeicher').valueChanges.subscribe(value => {
      if(value === true) {
        this.buttonsTypes = ['klein', 'gross_batterie'];
      }
      else {
        this.buttonsTypes = ['klein', 'gross'];
      }
      this.setSelection(value === true ? 'gross_batterie' : 'gross');

      // fake loading animation to show that something changed
      this.loading = true;
      setTimeout(() => this.loading = false, 200);
    });
  }

  /**
   * @inheritdoc
   */
  ngAfterViewInit() {
    setTimeout(() => this.updateHeights(), 200);
  }

  /**
   * Set selection of the balcony system
   * @param config
   * @param newResult
   */
  setSelection(config?: string , newResult?: Result) {
    if(!newResult) {
      newResult = this.anfrage.result;
    }

    // find all configurations in the result
    const systems = newResult?.result?.data?.photovoltaic_systems;

    if(systems?.length > 0) {
      this.configurations = {
        klein: {type: 'klein', config: systems.find(
          configuration => configuration.eigennutzung.flaeche === 2)
        },
        gross: {type: 'gross', config: systems.find(
          configuration => configuration.eigennutzung.flaeche === 4 && configuration.eigennutzung.batteriespeicher_kapazitaet === 0)
        },
        gross_batterie: {type: 'gross_batterie', config: systems.find(
          configuration => configuration.eigennutzung.flaeche === 4 && configuration.eigennutzung.batteriespeicher_kapazitaet > 0)
        }
      };

      // set the current configuration
      if (!config) {
        // check if we have a preset
        config = this.anfrage.configurationSelected ?? 'gross_batterie';
      }
      if(!this.configurations[config].config) {
        // fallback, use the first configuration that we have
        config = Object.keys(this.configurations).find(key => this.configurations[key].config !== undefined);
      }
      this.currentConfiguration = config as Optimierung;

      // update toggles in the form
      this.form.get('batteriespeicher').setValue(
        this.configurations[config].config.eigennutzung.batteriespeicher_kapazitaet > 0,
        {emitEvent: false}
      );

      this.form.get('verschattung').setValue(
        this.anfrage.userInput.verschattung === 'mittel' || this.anfrage.userInput.verschattung === true,
        {emitEvent: false}
      );

      // save selection to anfrage
      this.anfrage.configurationSelected = this.currentConfiguration;
      this.anfrage.result = newResult;
      this.requestService.setAnfrage(this.anfrage, true);
    }

    this.result = newResult;

    // set placeholder
    this.placeholder = this.result.result.placeholder[this.currentConfiguration] ?? {};

    setTimeout(() => this.updateHeights(), 0);
  }

  /**
   * recalculate the result with the given input from the resultpage
   */
  reCalculate(): void {
    this.anfrage.userInput.verschattung = this.form.get('verschattung').value;
    this.requestService.setAnfrage(this.anfrage);

    this.loading = true;
    this.backendService.getRequestId().subscribe(requestId => {
      this.backendService.result(this.anfrage, requestId).subscribe((result: Result) => {
        this.loading = false;
        this.toggle.price = false;

        this.updateHeights();

        // update the result
        this.setSelection(null, result);
      });
    });
  }

  /**
   * save the form with the elictricity price.
   * @param data
   */
  saveForm(data: {[key: string]: any}): void {
    // set data from form on anfrage
    this.anfrage.userInput = {...this.anfrage.userInput, ...data};

    this.reCalculate();
  }

  /**
   * (re)calculate the (min-)height of the compare and price flipper
   */
  @HostListener("window:resize", [])
  updateHeights(): void {
    if(!this.frontCardCompare || !this.backCardCompare) {
      // avoid errors
      return;
    }

    // compare
    this.minHeightCompare = this.frontCardCompare.nativeElement.offsetHeight;
    if(this.toggle.compare) {
      this.minHeightCompare = this.backCardCompare.nativeElement.offsetHeight;
    }

    // price
    this.minHeightPrice = 0;
    if(this.toggle.price) {
      this.minHeightPrice = this.backCardPrice.nativeElement.offsetHeight;
    }
  }

  /**
   * flip the compare view
   */
  flipCompare(): void {
    this.toggle.compare = !this.toggle.compare;

    if(this.toggle.compare) {
      this.elementService.scrollToElement(this.devices.nativeElement);
    }
    this.updateHeights();
  }

  /**
   * flip the price form
   */
  flipPrice(): void {
    this.toggle.price = !this.toggle.price;

    this.updateHeights();
  }
}
